// TicketsContext.js — payout review queue state
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { listTickets } from 'api/tickets';

const TicketsContext = createContext();

const DEFAULT_FILTERS = {
  status: '',
  type: 'withdraw',
  method: '',
  search: '',
};

export const TicketsProvider = ({ children }) => {
  const { isLoggedIn, user } = useAuth();

  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const fetchTickets = useCallback(
    async (overrideFilters = {}, overridePage = 1) => {
      setIsLoading(true);
      try {
        const params = {};
        const merged = { ...filters, ...overrideFilters };

        // Only send non-empty params to avoid sending empty strings
        Object.entries(merged).forEach(([key, val]) => {
          if (val !== '' && val !== null && val !== undefined) {
            params[key] = val;
          }
        });

        params.page = overridePage;
        params.limit = 20;

        const { data } = await listTickets(params);
        setTickets(data.data ?? []);
        setTotal(data.total ?? 0);
        setPage(overridePage);
      } catch (err) {
        console.error('TicketsContext: fetchTickets failed', err);
      } finally {
        setIsLoading(false);
      }
    },
    [filters],
  );

  // Update a single ticket in state after a PATCH (optimistic update)
  const updateTicketInState = (id, updatedTicket) => {
    setTickets(prev =>
      prev.map(t => (t.id === id ? { ...t, ...updatedTicket } : t)),
    );
  };

  const applyFilters = (newFilters, newPage = 1) => {
    setFilters(f => ({ ...f, ...newFilters }));
    fetchTickets(newFilters, newPage);
  };

  const refreshTickets = () => fetchTickets(filters, page);

  useEffect(() => {
    // Only fetch when user is an admin or agent and is logged in
    if (!isLoggedIn || !user) return;
    if (user.role !== 'admin' && user.role !== 'agent') return;
    fetchTickets({}, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, user]);

  return (
    <TicketsContext.Provider
      displayName='Tickets Context'
      value={{
        tickets,
        total,
        page,
        isLoading,
        filters,
        fetchTickets,
        applyFilters,
        refreshTickets,
        updateTicketInState,
      }}
    >
      {children}
    </TicketsContext.Provider>
  );
};

export const useTickets = () => useContext(TicketsContext);
